import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Form, Row, Col, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';

interface Proje {
  projeID: number;
  projeAdi: string;
  projeDurumu: string;
  aktifMi: boolean;
}

interface Sozlesme {
  sozlesmeID: number;
  sozlesmeTakipNo: string;
  sozlesmeTarihi: string;
  rezerveBitisTarihi?: string;
  sozlesmeDurumu: string;
  sozlesmeFiyati: number;
  planlananOdeme: number;
  ortalamaVade?: number;
  ozelSartlar?: string;
  aktifMi: boolean;
  eklemeTarihi: string;
  onayDurumu?: 'Bekliyor' | 'Onaylandi' | 'Reddedildi';
  bekleyenTalepTuru?: 'Onay' | 'Degisiklik' | 'Iptal' | null;
  cari?: {
    cariID: number;
    adiSoyadi: string;
    telefon: string;
    email?: string;
  };
  daire?: {
    daireID: number;
    daireNo: string;
    blok?: {
      blokAdi: string;
      proje?: {
        projeID: number;
        projeAdi: string;
      };
    };
  };
}

const Sozlesmeler: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sozlesmeler, setSozlesmeler] = useState<Sozlesme[]>([]);
  const [projeler, setProjeler] = useState<Proje[]>([]);
  const [odemeOzetleri, setOdemeOzetleri] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterDurum, setFilterDurum] = useState<string[]>([]);
  const [filterProje, setFilterProje] = useState<string>('');
  const [filterOdemePlan, setFilterOdemePlan] = useState<string>('');
  const [filterOnay, setFilterOnay] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);

  useEffect(() => {
    fetchSozlesmeler();
    fetchProjeler();
    fetchOdemeOzetleri();
  }, []);

  const fetchSozlesmeler = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/Sozlesmeler');
      setSozlesmeler(response.data);
    } catch (error) {
      toast.error(t('msg.sozlesmeYuklemeHata'));
      console.error('Sözleşme yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOdemeOzetleri = async () => {
    try {
      const response = await axiosInstance.get('/api/Sozlesmeler/odeme-ozetler');
      const map: Record<string, number> = {};
      (response.data as { sozlesmeTakipNo: string; toplamOdenen: number }[]).forEach(o => {
        map[o.sozlesmeTakipNo] = o.toplamOdenen;
      });
      setOdemeOzetleri(map);
    } catch {
      // sessiz hata
    }
  };

  const fetchProjeler = async () => {
    try {
      const response = await axiosInstance.get('/api/Projeler');
      setProjeler(response.data);
    } catch (error) {
      console.error('Proje yükleme hatası:', error);
    }
  };

  const DURUM_CONFIG: Record<string, { border: string; bg: string; badgeBg: string; badgeColor: string }> = {
    'Sözleşme':   { border: '#3b82f6', bg: '#eff6ff', badgeBg: '#dbeafe', badgeColor: '#1d4ed8' },
    'Rezerve':    { border: '#f59e0b', bg: '#fffbeb', badgeBg: '#fef9c3', badgeColor: '#ca8a04' },
    'ArsaSahibi': { border: '#8b5cf6', bg: '#f5f3ff', badgeBg: '#ede9fe', badgeColor: '#6d28d9' },
    'Taşeron':    { border: '#0ea5e9', bg: '#f0f9ff', badgeBg: '#e0f2fe', badgeColor: '#0369a1' },
  };

  const getDurumConfig = (durum: string) =>
    DURUM_CONFIG[durum] ?? { border: '#64748b', bg: '#f8fafc', badgeBg: '#f1f5f9', badgeColor: '#475569' };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const handleExportToExcel = () => {
    try {
      // Excel için veri hazırlama
      const excelData = filteredSozlesmeler.map((sozlesme) => ({
        'Müşteri': sozlesme.cari?.adiSoyadi || 'N/A',
        'Proje': sozlesme.daire?.blok?.proje?.projeAdi || 'N/A',
        'Blok': sozlesme.daire?.blok?.blokAdi || 'N/A',
        'Daire': sozlesme.daire?.daireNo || 'N/A',
        'Durum': sozlesme.sozlesmeDurumu,
        'Sözleşme Tarihi': formatDate(sozlesme.sozlesmeTarihi),
        'Fiyat': sozlesme.sozlesmeFiyati
      }));

      // Worksheet oluştur
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Kolon genişliklerini ayarla
      const colWidths = [
        { wch: 25 }, // Müşteri
        { wch: 20 }, // Proje
        { wch: 15 }, // Blok
        { wch: 10 }, // Daire
        { wch: 15 }, // Durum
        { wch: 15 }, // Sözleşme Tarihi
        { wch: 15 }  // Fiyat
      ];
      ws['!cols'] = colWidths;

      // Workbook oluştur
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sözleşmeler');

      // Dosya adı (tarihli)
      const fileName = `Sozlesmeler_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;

      // Excel dosyasını indir
      XLSX.writeFile(wb, fileName);

      toast.success(t('msg.excelAktarildi', { count: filteredSozlesmeler.length }));
    } catch (error) {
      toast.error(t('msg.excelHata'));
      console.error('Excel export hatası:', error);
    }
  };

  const getOdemePlanDurumu = (s: Sozlesme): 'tam' | 'eksik' | 'fazla' => {
    if (s.planlananOdeme === s.sozlesmeFiyati) return 'tam';
    if (s.planlananOdeme < s.sozlesmeFiyati) return 'eksik';
    return 'fazla';
  };

  // Filtreleme
  const filteredSozlesmeler = sozlesmeler.filter(sozlesme => {
    const matchesSearch = searchTerm === '' ||
      sozlesme.sozlesmeTakipNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sozlesme.cari?.adiSoyadi.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sozlesme.daire?.daireNo.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDurum = filterDurum.length === 0 || filterDurum.includes(sozlesme.sozlesmeDurumu);
    const matchesProje = filterProje === '' || sozlesme.daire?.blok?.proje?.projeID?.toString() === filterProje;
    const matchesOdemePlan = filterOdemePlan === '' || getOdemePlanDurumu(sozlesme) === filterOdemePlan;
    const matchesOnay = filterOnay === ''
      || (filterOnay === 'talep' && !!sozlesme.bekleyenTalepTuru)
      || sozlesme.onayDurumu === filterOnay;

    return matchesSearch && matchesDurum && matchesProje && matchesOdemePlan && matchesOnay;
  });

  const totalPages = Math.max(1, Math.ceil(filteredSozlesmeler.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pagedSozlesmeler = filteredSozlesmeler.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleFilterChange = (fn: () => void) => { fn(); setCurrentPage(1); };

  return (
    <>
      <style>{`
        .sz-wrap { background: #f8fafc; min-height: 100vh; padding: 28px 32px; font-family: 'Inter', 'Segoe UI', sans-serif; }
        .sz-page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 40px; }
        .sz-page-title { font-size: 22px; font-weight: 700; color: #0f172a; letter-spacing: -0.4px; margin: 0 0 4px 0; }
        .sz-page-breadcrumb { font-size: 13px; color: #6b7280; margin-bottom: 4px; }
        .sz-page-breadcrumb .sep { margin: 0 6px; }
        .sz-page-breadcrumb .current { color: #1a1a1a; font-weight: 500; }
        .sz-page-subtitle { font-size: 13.5px; color: #64748b; margin: 0; }
        .sz-stat-row { display: flex; gap: 16px; margin-bottom: 24px; }
        .sz-stat-card { flex: 1; background: #fff; border-radius: 12px; border: 1px solid #e5e7eb; box-shadow: 0 1px 3px rgba(0,0,0,0.05); padding: 16px 20px; display: flex; align-items: center; gap: 14px; }
        .sz-stat-icon { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .sz-stat-num { font-size: 24px; font-weight: 700; color: #111827; line-height: 1; font-variant-numeric: tabular-nums; }
        .sz-stat-label { font-size: 13px; color: #6b7280; margin-top: 4px; font-weight: 500; }
        .sz-filter-card { background: #fff; border-radius: 14px; border: 1.5px solid #f1f5f9; box-shadow: 0 1px 4px rgba(15,23,42,0.05); padding: 16px 20px; margin-bottom: 24px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .sz-search-wrap { position: relative; flex: 1; min-width: 280px; }
        .sz-search-icon { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: #94a3b8; font-size: 13px; pointer-events: none; }
        .sz-search-input { width: 100%; background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 9px 14px 9px 38px; font-size: 13.5px; color: #0f172a; outline: none; transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit; box-sizing: border-box; }
        .sz-search-input:focus { border-color: #e67e22; box-shadow: 0 0 0 3px rgba(230,126,34,0.1); background: #fff; }
        .sz-search-input::placeholder { color: #94a3b8; }
        .sz-select { background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px 36px 8px 12px; font-size: 13.5px; color: #0f172a; outline: none; cursor: pointer; transition: border-color 0.15s; font-family: inherit; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; min-width: 150px; }
        .sz-select:focus { border-color: #e67e22; box-shadow: 0 0 0 3px rgba(230,126,34,0.1); background-color: #fff; }
        .sz-btn-clear { background: none; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 9px 18px; font-size: 13px; color: #64748b; cursor: pointer; font-family: inherit; font-weight: 500; transition: all 0.15s; white-space: nowrap; }
        .sz-btn-clear:hover { border-color: #cbd5e1; color: #0f172a; background: #f8fafc; }
        .sz-btn-search { background: #e67e22; border: none; border-radius: 8px; padding: 10px 20px; font-size: 13px; color: #fff; cursor: pointer; font-family: inherit; font-weight: 600; transition: background 0.15s, box-shadow 0.15s; white-space: nowrap; letter-spacing: 0.3px; box-shadow: 0 4px 12px rgba(230,126,34,0.3); }
        .sz-durum-chips { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .sz-durum-label { font-size: 11px; font-weight: 600; color: #94a3b8; letter-spacing: 0.5px; white-space: nowrap; text-transform: uppercase; margin-right: 2px; }
        .sz-durum-chip { display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; cursor: pointer; border: 1.5px solid; transition: all 0.15s; white-space: nowrap; font-family: inherit; background: #f8fafc; }
        .sz-btn-search:hover { background: #c9621a; box-shadow: 0 4px 12px rgba(230,126,34,0.3); }
        .sz-list-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
        .sz-list-title { font-size: 16px; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: 10px; }
        .sz-count-chip { background: #f1f5f9; color: #64748b; border-radius: 20px; padding: 2px 10px; font-size: 12px; font-weight: 600; }
        .sz-list-actions { display: flex; gap: 10px; }
        .sz-btn-new { background: #cd6906; border: none; border-radius: 8px; padding: 10px 20px; font-size: 13px; color: #fff; cursor: pointer; font-family: inherit; font-weight: 600; transition: background 0.15s, box-shadow 0.15s; display: flex; align-items: center; gap: 7px; box-shadow: 0 4px 12px rgba(205,105,6,0.3); white-space: nowrap; }
        .sz-btn-new:hover { background: #b85d05; }
        .sz-btn-dl { background: #fff; border: 1.5px solid #1d6f42; border-radius: 8px; padding: 9px 18px; font-size: 13px; color: #1d6f42; cursor: pointer; font-family: inherit; font-weight: 600; transition: all 0.15s; display: flex; align-items: center; gap: 7px; box-shadow: 0 1px 3px rgba(0,0,0,0.07); white-space: nowrap; }
        .sz-btn-dl:hover { background: #1d6f42; color: #fff; }
        .sz-btn-dl:disabled { opacity: 0.4; cursor: not-allowed; }
        .sz-table-cols { display: flex; align-items: center; padding: 0 24px 10px; gap: 0; }
        .sz-th { font-size: 11px; font-weight: 600; color: #94a3b8; letter-spacing: 0.5px; }
        .sz-th.customer { flex: 0 0 220px; }
        .sz-th.divider { width: 41px; flex-shrink: 0; }
        .sz-th.proje { flex: 0 0 180px; }
        .sz-th.daire { flex: 0 0 110px; }
        .sz-th.durum { flex: 0 0 130px; }
        .sz-th.tarih { flex: 0 0 130px; }
        .sz-th.fiyat { flex: 1; }
        .sz-th.islem { flex: 0 0 48px; }
        .sz-panel { background: #fff; border-radius: 14px; border: 1.5px solid #f1f5f9; box-shadow: 0 1px 4px rgba(15,23,42,0.05); margin-bottom: 8px; display: flex; align-items: stretch; overflow: visible; transition: box-shadow 0.15s, transform 0.12s; position: relative; }
        .sz-panel:hover { background: #fef9f4; box-shadow: 0 4px 18px rgba(15,23,42,0.10); transform: translateY(-1px); cursor: pointer; }
        .sz-panel-accent { display: none; }
        .sz-panel-body { display: flex; align-items: center; padding: 16px 20px; flex: 1; gap: 0; }
        .sz-col-customer { flex: 0 0 220px; overflow: hidden; }
        .sz-customer-name { font-size: 14px; font-weight: 700; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sz-customer-sub { font-size: 12px; color: #64748b; margin-top: 2px; }
        .sz-takipno { font-size: 11px; color: #cbd5e1; margin-top: 2px; font-weight: 500; letter-spacing: 0.3px; }
        .sz-divider { width: 1px; background: #f1f5f9; align-self: stretch; margin: 0 20px; flex-shrink: 0; }
        .sz-col-proje { flex: 0 0 180px; overflow: hidden; }
        .sz-proje-name { font-size: 13px; font-weight: 600; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sz-blok-name { font-size: 12px; color: #64748b; margin-top: 4px; display: flex; align-items: center; gap: 4px; }
        .sz-col-daire { flex: 0 0 110px; display: flex; align-items: center; }
        .sz-daire-chip { background: #f1f5f9; color: #475569; font-size: 12px; font-weight: 600; border-radius: 8px; padding: 5px 12px; white-space: nowrap; }
        .sz-col-durum { flex: 0 0 130px; display: flex; flex-direction: column; align-items: flex-start; justify-content: center; }
        .sz-badge { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; border-radius: 20px; padding: 4px 12px; }
        .sz-badge-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
        .sz-col-tarih { flex: 0 0 130px; }
        .sz-tarih-label { font-size: 11px; color: #94a3b8; font-weight: 500; letter-spacing: 0.3px; text-transform: uppercase; }
        .sz-tarih-val { font-size: 13px; font-weight: 600; color: #334155; margin-top: 3px; }
        .sz-col-fiyat { flex: 1; }
        .sz-fiyat-label { font-size: 11px; color: #94a3b8; font-weight: 500; letter-spacing: 0.3px; text-transform: uppercase; }
        .sz-fiyat-val { font-size: 15px; font-weight: 700; color: #0f172a; margin-top: 3px; letter-spacing: -0.3px; }
        .sz-col-islem { flex: 0 0 100px; display: flex; align-items: center; justify-content: flex-end; }
        .sz-btn-detay { background: transparent; border: 1px solid #e67e22; border-radius: 6px; padding: 6px 12px; font-size: 13px; font-weight: 600; color: #e67e22; cursor: pointer; font-family: inherit; transition: all 0.15s; display: flex; align-items: center; gap: 7px; white-space: nowrap; }
        .sz-btn-detay:hover { background: #e67e22; color: #fff; }
        .sz-empty { background: #fff; border-radius: 14px; border: 1.5px solid #f1f5f9; padding: 64px 24px; text-align: center; }
        .sz-empty-icon { font-size: 42px; margin-bottom: 14px; opacity: 0.22; }
        .sz-empty-text { font-size: 14px; color: #94a3b8; font-weight: 500; }
        .sz-loading { background: #fff; border-radius: 14px; border: 1.5px solid #f1f5f9; padding: 64px 24px; text-align: center; }
        @keyframes sz-spin { to { transform: rotate(360deg); } }
        .sz-spinner { width: 32px; height: 32px; border: 3px solid #f1f5f9; border-top-color: #e67e22; border-radius: 50%; animation: sz-spin 0.8s linear infinite; margin: 0 auto 14px; }
        .sz-loading-text { font-size: 14px; color: #94a3b8; font-weight: 500; }
        /* Responsive */
        @media (max-width: 1200px) {
          .sz-stat-row { flex-wrap: wrap; }
          .sz-stat-card { flex: 1 1 calc(50% - 8px); min-width: 0; }
          .sz-table-cols { display: none; }
          .sz-panel-body { flex-wrap: wrap; gap: 12px 16px; }
          .sz-col-customer, .sz-col-proje, .sz-col-daire, .sz-col-durum, .sz-col-tarih, .sz-col-fiyat, .sz-col-islem { flex: unset; width: auto; }
          .sz-col-customer { flex: 1 1 180px; }
          .sz-divider { display: none; }
          .sz-col-proje { flex: 1 1 150px; }
        }
        .sz-pagination { display: flex; align-items: center; justify-content: space-between; margin-top: 20px; padding: 14px 20px; background: #fff; border-radius: 12px; border: 1.5px solid #f1f5f9; box-shadow: 0 1px 4px rgba(15,23,42,0.05); flex-wrap: wrap; gap: 12px; }
        .sz-pg-info { font-size: 13px; color: #64748b; }
        .sz-pg-info strong { color: #0f172a; }
        .sz-pg-controls { display: flex; align-items: center; gap: 4px; }
        .sz-pg-btn { width: 34px; height: 34px; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; border: 1.5px solid #e2e8f0; background: #fff; color: #475569; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; font-family: inherit; }
        .sz-pg-btn:hover:not(:disabled) { border-color: #e67e22; color: #e67e22; background: #fff8f0; }
        .sz-pg-btn.active { background: #e67e22; border-color: #e67e22; color: #fff; }
        .sz-pg-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        .sz-pg-size { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #64748b; }
        .sz-pg-size select { background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 7px; padding: 5px 28px 5px 10px; font-size: 13px; color: #0f172a; outline: none; cursor: pointer; font-family: inherit; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 9px center; }
        @media (max-width: 768px) {
          .sz-wrap { padding: 16px 12px; }
          .sz-stat-card { flex: 1 1 100%; }
          .sz-filter-card { flex-direction: column; align-items: stretch; }
          .sz-search-wrap { min-width: unset; }
          .sz-select { width: 100%; }
          .sz-list-header { flex-wrap: wrap; gap: 10px; }
          .sz-list-actions { width: 100%; justify-content: flex-end; }
          .sz-panel-body { flex-direction: column; gap: 10px; padding: 14px 16px; }
          .sz-col-customer, .sz-col-proje, .sz-col-daire, .sz-col-durum, .sz-col-tarih, .sz-col-fiyat, .sz-col-islem { flex: unset; width: 100%; }
          .sz-col-islem { justify-content: flex-start; }
          .sz-takipno { display: none; }
        }

      `}</style>

      <div className="sz-wrap">

        {/* Page Header */}
        <div className="sz-page-header">
          <div>
            <button
              onClick={() => navigate(-1)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '12px', fontWeight: 600, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 4, transition: 'color 0.15s', letterSpacing: '0.01em' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#0f172a')}
              onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
            >
              <i className="fas fa-arrow-left" style={{ fontSize: '10px' }}></i> {t('btn.geriDon')}
            </button>
            <h1 className="sz-page-title">{t('sozlesme.baslik')}</h1>
            <div className="sz-page-subtitle">{t('sozlesme.altBaslik')}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              className="sz-btn-dl"
              onClick={handleExportToExcel}
              disabled={filteredSozlesmeler.length === 0}
            >
              <svg width={15} height={15} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="3" width="20" height="18" rx="2" stroke="currentColor" strokeWidth="1.8"/>
                <path d="M8 3v18M16 3v18M2 9h20M2 15h20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
              {t('btn.excelIndir')}
            </button>
            <button className="sz-btn-new" onClick={() => navigate('/satis-sozlesmesi')}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              {t('sozlesme.yeni')}
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="sz-stat-row">
          <div className="sz-stat-card">
            <div className="sz-stat-icon" style={{ background: '#f9fafb' }}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#6b7280" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" /></svg>
            </div>
            <div>
              <div className="sz-stat-num">{sozlesmeler.length}</div>
              <div className="sz-stat-label">{t('sozlesme.toplam')}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}><span style={{ color: '#16a34a', fontWeight: 600 }}>↑ 2</span> bu ay eklendi</div>
            </div>
          </div>
          <div className="sz-stat-card">
            <div className="sz-stat-icon" style={{ background: '#f0fdf4' }}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#16a34a" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div>
              <div className="sz-stat-num">{sozlesmeler.filter(s => s.sozlesmeDurumu === 'Sözleşme').length}</div>
              <div className="sz-stat-label">{t('sozlesme.aktif')}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}><span style={{ color: '#16a34a', fontWeight: 600 }}>→ 0</span> değişim yok</div>
            </div>
          </div>
          <div className="sz-stat-card">
            <div className="sz-stat-icon" style={{ background: '#fffbeb' }}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#d97706" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div>
              <div className="sz-stat-num">{sozlesmeler.filter(s => s.sozlesmeDurumu === 'Rezerve').length}</div>
              <div className="sz-stat-label">{t('durum.rezerve')}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}><span style={{ color: '#ca8a04', fontWeight: 600 }}>↑ 1</span> bu ay eklendi</div>
            </div>
          </div>
          <div className="sz-stat-card">
            <div className="sz-stat-icon" style={{ background: '#eff6ff' }}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#2563eb" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" /></svg>
            </div>
            <div>
              <div className="sz-stat-num" style={{ fontSize: 17 }}>
                {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(
                  filteredSozlesmeler.reduce((sum, s) => sum + (s.sozlesmeFiyati || 0), 0)
                )}
              </div>
              <div className="sz-stat-label">{t('sozlesme.toplamDeger')}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}><span style={{ color: '#16a34a', fontWeight: 600 }}>↑ %8</span> geçen aya göre</div>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="sz-filter-card" onClick={e => e.stopPropagation()}>
          <div className="sz-search-wrap">
            <i className="fas fa-search sz-search-icon"></i>
            <input
              type="text"
              className="sz-search-input"
              placeholder={t('label.ara')}
              value={searchTerm}
              onChange={e => handleFilterChange(() => setSearchTerm(e.target.value))}
            />
          </div>
          <div className="sz-durum-chips">
            <span className="sz-durum-label">{t('label.durum')}:</span>
            {(['Sözleşme', 'Rezerve', 'ArsaSahibi', 'Taşeron'] as const).map(d => {
              const cfg = getDurumConfig(d);
              const isActive = filterDurum.includes(d);
              const label = t('sozlesme.durum.' + d, d === 'ArsaSahibi' ? 'Arsa Sahibi' : d);
              return (
                <button
                  key={d}
                  className="sz-durum-chip"
                  style={{
                    borderColor: isActive ? cfg.border : '#e2e8f0',
                    background: isActive ? cfg.badgeBg : '#f8fafc',
                    color: isActive ? cfg.badgeColor : '#64748b',
                  }}
                  onClick={() =>
                    handleFilterChange(() =>
                      setFilterDurum(prev =>
                        prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]
                      )
                    )
                  }
                >
                  {isActive && (
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.border, flexShrink: 0, display: 'inline-block' }} />
                  )}
                  {label}
                </button>
              );
            })}
          </div>
          <select
            className="sz-select"
            value={filterProje}
            onChange={e => handleFilterChange(() => setFilterProje(e.target.value))}
          >
            <option value="">{t('label.proje')} — {t('label.tumu', 'Tümü')}</option>
            {projeler.map(proje => (
              <option key={proje.projeID} value={proje.projeID?.toString()}>
                {proje.projeAdi}
              </option>
            ))}
          </select>
          <select
            className="sz-select"
            value={filterOdemePlan}
            onChange={e => handleFilterChange(() => setFilterOdemePlan(e.target.value))}
          >
            <option value="">{t('sozlesme.odemePlani', 'Ödeme Planı')} — {t('label.tumu', 'Tümü')}</option>
            <option value="tam">{t('sozlesme.tamPlanlanmis', 'Tam Planlanmış')}</option>
            <option value="eksik">{t('sozlesme.eksikPlanlanmis', 'Eksik Planlanmış')}</option>
            <option value="fazla">{t('sozlesme.fazlaPlanlanmis', 'Fazla Planlanmış')}</option>
          </select>
          <select
            className="sz-select"
            value={filterOnay}
            onChange={e => handleFilterChange(() => setFilterOnay(e.target.value))}
          >
            <option value="">Onay — Tümü</option>
            <option value="talep">Bekleyen talebi olanlar</option>
            <option value="Bekliyor">Onay bekleyenler</option>
            <option value="Reddedildi">Reddedilenler</option>
            <option value="Onaylandi">Onaylananlar</option>
          </select>
          <button
            className="sz-btn-clear"
            onClick={() => { setSearchTerm(''); setFilterDurum([]); setFilterProje(''); setFilterOdemePlan(''); setFilterOnay(''); setCurrentPage(1); }}
          >
            {t('btn.temizle')}
          </button>
          <button className="sz-btn-search" onClick={fetchSozlesmeler}>{t('btn.ara', 'Ara')}</button>
        </div>

        {/* List Header */}
        <div className="sz-list-header">
          <div className="sz-list-title">
            {t('sozlesme.listesi')}
            <span className="sz-count-chip">{filteredSozlesmeler.length} {t('tablo.toplamKayit')}</span>
          </div>
        </div>

        {/* Column Headers */}
        {!loading && filteredSozlesmeler.length > 0 && (
          <div className="sz-table-cols">
          <span className="sz-th customer">{t('label.musteri')}</span>
            <span className="sz-th divider" />
            <span className="sz-th proje">{t('label.projeBlok')}</span>
            <span className="sz-th daire">{t('label.daire')}</span>
            <span className="sz-th durum">{t('label.durum')}</span>
            <span className="sz-th tarih">{t('label.sozlesmeTarihi')}</span>
            <span className="sz-th fiyat">{t('label.fiyat')}</span>
            <span className="sz-th islem" style={{ flex: '0 0 100px' }}>İşlemler</span>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="sz-loading">
            <div className="sz-spinner" />
            <div className="sz-loading-text">{t('sozlesme.yukleniyor')}</div>
          </div>
        ) : filteredSozlesmeler.length === 0 ? (
          <div className="sz-empty">
            <div className="sz-empty-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#cbd5e1" width="42" height="42"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
            </div>
            <div className="sz-empty-text">
              {searchTerm || filterDurum.length > 0 || filterProje
                ? t('msg.filtreUygunKayitYok')
                : t('tablo.kayitYok')}
            </div>
          </div>
        ) : (
          pagedSozlesmeler.map(sozlesme => {
            const cfg = getDurumConfig(sozlesme.sozlesmeDurumu);
            return (
              <div
                key={sozlesme.sozlesmeID}
                className="sz-panel"
              >
                <div className="sz-panel-body">

                  {/* Müşteri */}
                  <div className="sz-col-customer">
                    <div className="sz-customer-name">{sozlesme.cari?.adiSoyadi || '—'}</div>
                    {sozlesme.cari?.telefon && (
                      <div className="sz-customer-sub">{sozlesme.cari.telefon}</div>
                    )}
                    <div className="sz-takipno">#{sozlesme.sozlesmeTakipNo}</div>
                  </div>

                  <div className="sz-divider" />

                  {/* Proje / Blok */}
                  <div className="sz-col-proje">
                    <div className="sz-proje-name">{sozlesme.daire?.blok?.proje?.projeAdi || '—'}</div>
                    {sozlesme.daire?.blok?.blokAdi && (
                      <div className="sz-blok-name">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#94a3b8" width="11" height="11"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg>
                        {sozlesme.daire.blok.blokAdi}
                      </div>
                    )}
                  </div>

                  {/* Daire */}
                  <div className="sz-col-daire">
                    <span className="sz-daire-chip">
                      {sozlesme.daire?.daireNo ? `Daire ${sozlesme.daire.daireNo}` : '—'}
                    </span>
                  </div>

                  {/* Durum */}
                  <div className="sz-col-durum">
                    <span className="sz-badge" style={{ background: cfg.badgeBg, color: cfg.badgeColor }}>
                      <span className="sz-badge-dot" style={{ background: cfg.border }} />
                      {t('sozlesme.durum.' + sozlesme.sozlesmeDurumu, sozlesme.sozlesmeDurumu === 'ArsaSahibi' ? 'Arsa Sahibi' : sozlesme.sozlesmeDurumu)}
                    </span>
                    {(() => {
                      const onay = sozlesme.onayDurumu === 'Bekliyor' ? { metin: 'Onay Bekliyor', renk: '#b45309', zemin: '#fef3c7' }
                        : sozlesme.onayDurumu === 'Reddedildi' ? { metin: 'Reddedildi', renk: '#b91c1c', zemin: '#fee2e2' }
                        : sozlesme.bekleyenTalepTuru === 'Degisiklik' ? { metin: 'Değişiklik Talebi', renk: '#1d4ed8', zemin: '#dbeafe' }
                        : sozlesme.bekleyenTalepTuru === 'Iptal' ? { metin: 'İptal Talebi', renk: '#b91c1c', zemin: '#fee2e2' }
                        : null;
                      return onay && (
                        <span className="sz-badge" style={{ background: onay.zemin, color: onay.renk, marginTop: 4 }}>
                          <i className="fas fa-hourglass-half" style={{ fontSize: 10, marginRight: 4 }}></i>{onay.metin}
                        </span>
                      );
                    })()}
                  </div>

                  {/* Tarih */}
                  <div className="sz-col-tarih">
                    <div className="sz-tarih-label">{t('label.tarih')}</div>
                    <div className="sz-tarih-val">{formatDate(sozlesme.sozlesmeTarihi)}</div>
                  </div>

                  {/* Fiyat */}
                  <div className="sz-col-fiyat">
                    <div className="sz-fiyat-label">{t('label.sozlesmeFiyati')}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                      <span className="sz-fiyat-val">{formatCurrency(sozlesme.sozlesmeFiyati)}</span>
                      {(() => {
                        const durum = getOdemePlanDurumu(sozlesme);
                        if (durum === 'tam') return (
                          <span title="Planlanan ödeme tam" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 6, background: '#dcfce7' }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          </span>
                        );
                        if (durum === 'eksik') return (
                          <span title={`Eksik: ${formatCurrency(sozlesme.sozlesmeFiyati - sozlesme.planlananOdeme)}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 6, background: '#fee2e2' }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                          </span>
                        );
                        return (
                          <span title={`Fazla: ${formatCurrency(sozlesme.planlananOdeme - sozlesme.sozlesmeFiyati)}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 6, background: '#fef9c3' }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#ca8a04" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                          </span>
                        );
                      })()}
                    </div>
                    {(() => {
                      const odenen = odemeOzetleri[sozlesme.sozlesmeTakipNo] ?? 0;
                      if (odenen <= 0 || sozlesme.sozlesmeFiyati <= 0) return null;
                      const yuzde = Math.min(100, (odenen / sozlesme.sozlesmeFiyati) * 100);
                      return (
                        <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 700, marginTop: 2 }}>
                          %{yuzde.toFixed(1)} ödendi
                        </div>
                      );
                    })()}
                  </div>

                  {/* İşlemler */}
                  <div className="sz-col-islem">
                    <button
                      className="sz-btn-detay"
                      onClick={() => navigate(`/sozlesme-detay/${sozlesme.sozlesmeTakipNo}`)}
                    >
                      <i className="fas fa-eye" style={{ fontSize: 12 }}></i>
                      {t('sozlesme.detaylar')}
                    </button>
                  </div>

                </div>
              </div>
            );
          })
        )}

        {/* Pagination */}
        {!loading && filteredSozlesmeler.length > 0 && (
          <div className="sz-pagination">
            <div className="sz-pg-info">
              <strong>{(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filteredSozlesmeler.length)}</strong> / {filteredSozlesmeler.length} {t('tablo.toplamKayit')}
            </div>
            <div className="sz-pg-controls">
              <button className="sz-pg-btn" onClick={() => setCurrentPage(1)} disabled={safePage === 1} title="İlk sayfa">«</button>
              <button className="sz-pg-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1} title={t('tablo.onceki')}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
                .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === '...'
                    ? <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: '#94a3b8', fontSize: 13 }}>…</span>
                    : <button key={p} className={`sz-pg-btn${safePage === p ? ' active' : ''}`} onClick={() => setCurrentPage(p as number)}>{p}</button>
                )
              }
              <button className="sz-pg-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} title={t('tablo.sonraki')}>›</button>
              <button className="sz-pg-btn" onClick={() => setCurrentPage(totalPages)} disabled={safePage === totalPages} title="Son sayfa">»</button>
            </div>
            <div className="sz-pg-size">
              {t('label.sayfaBasi')}
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default Sozlesmeler;